/**
 * Instantiates checklist tasks from templates (idempotent per workflowKey).
 */

import { getTemplateByWorkflowKey, WORKFLOW_KEYS } from '../../data/workflowChecklistTemplates';
import { normalizeTask, TASK_SOURCE, TASK_STATUS } from '../../workflow/taskModel';
import {
  loadTasksForResident,
  saveTasksForResident,
  isWorkflowInstantiated,
  markWorkflowInstantiated,
} from './TaskPersistence';

/**
 * Compute due date string YYYY-MM-DD from anchor + days.
 * @param {string} anchorIso YYYY-MM-DD
 * @param {number} dueDaysFromAnchor
 */
export function dueDateFromAnchor(anchorIso, dueDaysFromAnchor) {
  const datePart = anchorIso.split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + dueDaysFromAnchor);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Ensure workflow tasks exist for resident (idempotent).
 * @param {{ residentId: string|number, facilityId: string, workflowKey: string, anchorDateIso: string, createdBy?: string }} params
 * @returns {{ created: boolean, tasks: import('../../workflow/taskModel').WorkflowTask[], message?: string }}
 */
export function ensureWorkflow(params) {
  const { residentId, facilityId, workflowKey, anchorDateIso, createdBy = 'system' } = params;

  if (!residentId || !workflowKey || !anchorDateIso) {
    return { created: false, tasks: loadTasksForResident(residentId), message: 'missing_params' };
  }

  if (isWorkflowInstantiated(residentId, workflowKey)) {
    const existing = loadTasksForResident(residentId).filter((t) => t.workflowKey === workflowKey);
    return { created: false, tasks: existing, message: 'already_exists' };
  }

  const template = getTemplateByWorkflowKey(workflowKey);
  if (template.length === 0) {
    return { created: false, tasks: [], message: 'unknown_workflow' };
  }

  const now = new Date().toISOString();
  const newTasks = template.map((item) => {
    const dueAt = dueDateFromAnchor(anchorDateIso, item.dueDaysFromAnchor);
    const id = `wf_${workflowKey}_${residentId}_${item.templateId}`.replace(/[^a-zA-Z0-9_]/g, '_');

    return normalizeTask({
      id,
      facilityId,
      residentId,
      title: item.title,
      description: item.description,
      category: item.category,
      ownerRole: item.ownerRole,
      dueAt,
      priority: item.priority,
      status: TASK_STATUS.PENDING,
      source: TASK_SOURCE.SYSTEM,
      templateId: item.templateId,
      workflowKey,
      dependsOnTemplateIds: item.dependsOnTemplateIds,
      createdAt: now,
      updatedAt: now,
      createdBy,
    });
  });

  const all = [...loadTasksForResident(residentId), ...newTasks];
  saveTasksForResident(residentId, all);
  markWorkflowInstantiated(residentId, workflowKey);

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[WorkflowEngine] instantiated', { workflowKey, residentId, count: newTasks.length });
  }

  return { created: true, tasks: newTasks };
}

/**
 * Ensure both Stage 6 and 7A if needed (7A uses same anchor).
 */
export function ensureAdmissionAndSettling(residentId, facilityId, admissionDateIso, createdBy) {
  const a = ensureWorkflow({
    residentId,
    facilityId,
    workflowKey: WORKFLOW_KEYS.STAGE6_ADMISSION,
    anchorDateIso: admissionDateIso,
    createdBy,
  });
  const b = ensureWorkflow({
    residentId,
    facilityId,
    workflowKey: WORKFLOW_KEYS.STAGE7A_SETTLING,
    anchorDateIso: admissionDateIso,
    createdBy,
  });
  return { stage6: a, stage7a: b };
}
